import { getFullName } from "utils";

// Keeps a branch-name match ranked below a name match of the same quality.
const BRANCH_MATCH_PENALTY = 6;

interface FuzzyMatchState {
  score: number;
  cursor: number;
  previousIndex: number;
}

export const CLOSE_WITHOUT_SHARING_TOOLTIP = "Close without sharing";
const NO_BRANCH_LABEL = "No branch";
const MY_BRANCH_GROUP_LABEL = "Teammates in my branch";
const OTHER_BRANCHES_GROUP_LABEL = "Across other branches";
const SEARCH_RESULTS_GROUP_LABEL = "Search results";

interface TeammateGroup {
  label: string;
  teammates: CompanyMember[];
}

interface ListTeammateGroupsProps {
  matches: CompanyMember[];
  callerBranchId: number | null;
  isSearching: boolean;
  sharedRoleIdsAtOpen: ReadonlySet<number>;
}

interface SelectAllState {
  allSelected: boolean;
  someSelected: boolean;
}

// Names are never empty in the database but can be whitespace, so a blank name
// falls back to the email to keep the teammate identifiable.
export function getTeammateFullName(teammate: CompanyMember): string {
  const fullName = getFullName(
    teammate.user.first_name,
    teammate.user.last_name,
  ).trim();
  return fullName || teammate.user.email.trim();
}

export function getTeammateBranchName(teammate: CompanyMember): string {
  return teammate.branch_info?.name ?? NO_BRANCH_LABEL;
}

// The share endpoint takes role ids, so a teammate is identified by member.id,
// never member.user.id. A teammate with neither a name nor an email cannot be
// told apart in the picker, so they are left out.
export function listSelectableTeammates(
  members: CompanyMember[],
  callerRoleId: number,
): CompanyMember[] {
  return members.filter(
    (member) =>
      member.id !== callerRoleId && getTeammateFullName(member) !== "",
  );
}

/**
 * Subsequence match: every character of the query (spaces ignored) has to
 * appear in the target in order. Lower scores are better. Gaps between matched
 * characters cost points and matches at the start of a word earn some back.
 * Returns null when the query is not a subsequence of the target.
 */
export function fuzzyScore(query: string, target: string): number | null {
  const normalizedTarget = target.toLowerCase();
  const characters = Array.from(query.trim().toLowerCase()).filter(
    (character) => character !== " ",
  );

  const finalState = characters.reduce<FuzzyMatchState | null>(
    (state, character) => {
      if (state === null) return null;
      const foundIndex = normalizedTarget.indexOf(character, state.cursor);
      if (foundIndex === -1) return null;
      const gapPenalty =
        foundIndex === state.previousIndex + 1
          ? 0
          : foundIndex - state.cursor + 1;
      const startsWord =
        foundIndex === 0 || normalizedTarget[foundIndex - 1] === " ";
      return {
        score: state.score + gapPenalty - (startsWord ? 1 : 0),
        cursor: foundIndex + 1,
        previousIndex: foundIndex,
      };
    },
    { score: 0, cursor: 0, previousIndex: -2 },
  );
  return finalState === null ? null : finalState.score;
}

function getTeammateSearchScore(
  teammate: CompanyMember,
  query: string,
): number | null {
  const nameScore = fuzzyScore(query, getTeammateFullName(teammate));
  const branchScore = teammate.branch_info
    ? fuzzyScore(query, teammate.branch_info.name)
    : null;
  if (branchScore === null) return nameScore;
  const penalizedBranchScore = branchScore + BRANCH_MATCH_PENALTY;
  return nameScore === null
    ? penalizedBranchScore
    : Math.min(nameScore, penalizedBranchScore);
}

export function searchTeammates(
  teammates: CompanyMember[],
  query: string,
): CompanyMember[] {
  if (!query.trim()) return teammates;

  return teammates
    .map((teammate) => ({
      teammate,
      score: getTeammateSearchScore(teammate, query),
    }))
    .filter(
      (entry): entry is { teammate: CompanyMember; score: number } =>
        entry.score !== null,
    )
    .sort((a, b) => a.score - b.score)
    .map((entry) => entry.teammate);
}

export function isInCallerBranch(
  teammate: CompanyMember,
  callerBranchId: number | null,
): boolean {
  return callerBranchId !== null && teammate.branch === callerBranchId;
}

export function sortSharedFirst(
  teammates: CompanyMember[],
  sharedRoleIds: ReadonlySet<number>,
): CompanyMember[] {
  return [...teammates].sort(
    (a, b) => Number(sharedRoleIds.has(b.id)) - Number(sharedRoleIds.has(a.id)),
  );
}

/** "Select all" skips teammates who already have the template. */
export function listSelectAllRoleIds(
  visibleTeammates: CompanyMember[],
  sharedRoleIds: ReadonlySet<number>,
): number[] {
  return visibleTeammates
    .filter((teammate) => !sharedRoleIds.has(teammate.id))
    .map((teammate) => teammate.id);
}

export function buildShareConfirmationMessage(
  templateName: string,
  recipients: CompanyMember[],
): string {
  const recipientLabel =
    recipients.length === 1
      ? getTeammateFullName(recipients[0])
      : `${recipients.length} teammates`;
  return `“${templateName}” shared with ${recipientLabel}`;
}

/**
 * A search collapses everything into one ranked group. Otherwise teammates are
 * split into my branch and the rest, each with already-shared teammates first.
 */
export function listTeammateGroups(
  props: ListTeammateGroupsProps,
): TeammateGroup[] {
  const { matches, callerBranchId, isSearching, sharedRoleIdsAtOpen } = props;
  if (isSearching) {
    return matches.length > 0
      ? [{ label: SEARCH_RESULTS_GROUP_LABEL, teammates: matches }]
      : [];
  }
  const sorted = sortSharedFirst(matches, sharedRoleIdsAtOpen);
  return [
    {
      label: MY_BRANCH_GROUP_LABEL,
      teammates: sorted.filter((teammate) =>
        isInCallerBranch(teammate, callerBranchId),
      ),
    },
    {
      label: OTHER_BRANCHES_GROUP_LABEL,
      teammates: sorted.filter(
        (teammate) => !isInCallerBranch(teammate, callerBranchId),
      ),
    },
  ].filter((group) => group.teammates.length > 0);
}

export function getSelectAllState(
  selectAllRoleIds: number[],
  selectedRoleIds: ReadonlySet<number>,
): SelectAllState {
  const selectedCount = selectAllRoleIds.filter((roleId) =>
    selectedRoleIds.has(roleId),
  ).length;
  const allSelected =
    selectAllRoleIds.length > 0 && selectedCount === selectAllRoleIds.length;
  return { allSelected, someSelected: selectedCount > 0 && !allSelected };
}
